// 图片代理服务 - 解决CORS问题
export class ImageProxyService {
  // 代理服务器配置
  static PROXY_SERVER_URL = '' // 使用相对路径，通过Vite代理
  
  // 检查代理服务器是否可用
  static async checkProxyServer() {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 3000) // 减少超时时间
      
      const response = await fetch('/health', {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        }
      })
      
      clearTimeout(timeoutId)
      
      if (response.ok) {
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json()
          console.log('✅ 图片代理服务器连接正常:', data)
          return true
        } else {
          console.warn('⚠️ 代理服务器返回非JSON响应')
        }
      } else {
        console.warn('⚠️ 代理服务器响应异常:', response.status, response.statusText)
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('⚠️ 代理服务器连接超时')
      } else {
        console.warn('⚠️ 图片代理服务器不可用:', error.message)
      }
    }
    return false
  }
  
  // 获取代理后的图片URL
  static getProxiedImageUrl(originalUrl) {
    if (!originalUrl) return null
    
    // 如果已经是代理URL，直接返回
    if (originalUrl.includes('/proxy/image')) {
      return originalUrl
    }
    
    // 构建代理URL
    const encodedUrl = encodeURIComponent(originalUrl)
    return `/proxy/image?url=${encodedUrl}`
  }
  
  // 批量处理图片URL
  static processImageUrls(imageUrls) {
    if (!Array.isArray(imageUrls)) {
      return this.getProxiedImageUrl(imageUrls)
    }
    
    return imageUrls.map(url => this.getProxiedImageUrl(url))
  }
  
  // 预加载图片
  static async preloadImage(imageUrl, timeout = 10000) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      const timer = setTimeout(() => {
        reject(new Error('图片加载超时'))
      }, timeout)
      
      img.onload = () => {
        clearTimeout(timer)
        resolve(img)
      }
      
      img.onerror = (error) => {
        clearTimeout(timer)
        reject(error)
      }
      
      img.src = imageUrl
    })
  }
  
  // 智能图片加载策略
  static async loadImageWithFallback(imageUrl, options = {}) {
    const { maxRetries = 2, delay = 1000, useProxyFirst = false } = options
    
    // 优先直接加载策略（大多数情况下都能成功）
    if (!useProxyFirst) {
      try {
        console.log('🖼️ 尝试直接加载图片:', imageUrl)
        const img = await this.preloadImage(imageUrl, 15000)
        console.log('✅ 直接图片加载成功:', imageUrl)
        return img
      } catch (error) {
        console.warn('⚠️ 直接图片加载失败:', error.message)
        
        // 只有在直接加载失败且URL需要代理时才检查代理服务器
        if (this.shouldUseProxy(imageUrl)) {
          console.log('🔍 检查代理服务器可用性...')
          const proxyAvailable = await this.checkProxyServer()
          
          if (proxyAvailable) {
            const proxiedUrl = this.getProxiedImageUrl(imageUrl)
            try {
              console.log('🔄 尝试使用代理URL:', proxiedUrl)
              const img = await this.preloadImage(proxiedUrl, 15000)
              console.log('✅ 代理图片加载成功:', proxiedUrl)
              return img
            } catch (proxyError) {
              console.error('❌ 代理图片加载也失败:', proxyError.message)
            }
          } else {
            console.warn('⚠️ 代理服务器不可用，跳过代理尝试')
          }
        }
        
        // 所有方法都失败了
        throw error
      }
    }
    
    // 代理优先策略（仅在明确要求时使用）
    console.log('🔍 检查代理服务器可用性...')
    const proxyAvailable = await this.checkProxyServer()
    
    if (proxyAvailable && this.shouldUseProxy(imageUrl)) {
      const proxiedUrl = this.getProxiedImageUrl(imageUrl)
      try {
        console.log('🖼️ 优先使用代理URL加载:', proxiedUrl)
        const img = await this.preloadImage(proxiedUrl, 15000)
        console.log('✅ 代理图片加载成功:', proxiedUrl)
        return img
      } catch (error) {
        console.warn('⚠️ 代理图片加载失败，尝试直接加载:', error.message)
      }
    }
    
    // 代理失败或不可用，尝试直接加载
    try {
      console.log('🖼️ 尝试直接加载图片:', imageUrl)
      const img = await this.preloadImage(imageUrl, 15000)
      console.log('✅ 直接图片加载成功:', imageUrl)
      return img
    } catch (directError) {
      console.error('❌ 所有加载方法都失败了:', directError.message)
      throw directError
    }
  }
  
  // 判断是否应该使用代理
  static shouldUseProxy(imageUrl) {
    // 禁用图片代理功能
    // Service Worker已配置为使用no-cors模式处理跨域图片
    // 图片可以正常显示，虽然会有CORS警告，但不影响使用
    return false
  }
  
  // 带重试的图片加载（保持向后兼容）
  static async loadImageWithRetry(imageUrl, maxRetries = 3, delay = 1000) {
    return this.loadImageWithFallback(imageUrl, { maxRetries, delay })
  }
  
  // 辅助方法：延迟
  static delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
  
  // 验证图片URL格式
  static isValidImageUrl(url) {
    if (!url || typeof url !== 'string') return false
    
    // 检查是否是有效的URL
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }
  
  // 获取图片信息
  static async getImageInfo(imageUrl) {
    try {
      const img = await this.preloadImage(imageUrl, 5000)
      return {
        width: img.naturalWidth,
        height: img.naturalHeight,
        loaded: true
      }
    } catch (error) {
      return {
        width: 0,
        height: 0,
        loaded: false,
        error: error.message
      }
    }
  }
}

export default ImageProxyService
